import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Divider,
  Chip,
  OutlinedInput,
  Card,
  CardContent,
  Paper,
  Stack,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const UserForm = ({ user, roles, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    preferredName: '',
    middleName: '',
    noMiddleName: false,
    lastName: '',
    email: '',
    primaryAddressStreet: '',
    primaryAddressCity: '',
    primaryAddressZip: '',
    gender: '',
    district: '',
    school: '',
    graduationYear: '',
    tshirtSize: '',
    seekingTeam: false,
    optInCommunications: false,
    isActive: true,
    roleIds: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        preferredName: user.preferredName || '',
        middleName: user.middleName || '',
        noMiddleName: user.noMiddleName || false,
        lastName: user.lastName || '',
        email: user.email || '',
        primaryAddressStreet: user.primaryAddressStreet || '',
        primaryAddressCity: user.primaryAddressCity || '',
        primaryAddressZip: user.primaryAddressZip || '',
        gender: user.gender || '',
        district: user.district || '',
        school: user.school || '',
        graduationYear: user.graduationYear || '',
        tshirtSize: user.tshirtSize || '',
        seekingTeam: user.seekingTeam || false,
        optInCommunications: user.optInCommunications || false,
        isActive: user.isActive !== undefined ? user.isActive : true,
        roleIds: user.roleIds || [],
      });
    }
  }, [user]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Zip code validation function
  const handleZipCodeChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    
    setFormData(prev => ({
      ...prev,
      primaryAddressZip: numericValue
    }));
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      roleIds: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Middle name validation: either middle name must be filled OR "I don't have a middle name" must be checked
    if (!formData.middleName.trim() && !formData.noMiddleName) {
      newErrors.middleName = 'Please enter a middle name or check "No middle name"';
    }
    
    if (!formData.email.trim()) {
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const tshirtSizes = ['Youth Small', 'Youth Medium', 'Youth Large', 'Adult Small', 'Adult Medium', 'Adult Large', 'Adult XL', 'Adult XXL'];
  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({ length: 14 }, (_, i) => (currentYear + i).toString());
  
  // Check if user has student role
  const hasStudentRole = formData.roleIds && formData.roleIds.some(roleId => {
    const role = roles.find(r => r.id === roleId);
    return role && role.name === 'Student';
  });

  const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: '2px solid #009494' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        width: 40, 
        height: 40, 
        borderRadius: '50%', 
        backgroundColor: '#009494', 
        color: 'white',
        mr: 2
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: '#949494', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <Stack spacing={4}>
          {/* Basic Information Card */}
          <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #94e3e3' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader 
                icon={<PersonIcon />} 
                title="Basic Information" 
                subtitle="Personal details and contact information"
              />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#009494',
                        },
                      },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name (Legal)"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Preferred Name"
                    value={formData.preferredName}
                    onChange={handleChange('preferredName')}
                    helperText="Name you go by (e.g., Matt for Matthew)"
                    placeholder="Optional"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name (Legal)"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Middle Name"
                    value={formData.middleName}
                    onChange={handleChange('middleName')}
                    disabled={formData.noMiddleName}
                    error={!!errors.middleName}
                    helperText={errors.middleName}
                    placeholder={formData.noMiddleName ? "No middle name" : "Enter middle name"}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.noMiddleName}
                        onChange={handleChange('noMiddleName')}
                        sx={{
                          color: '#009494',
                          '&.Mui-checked': {
                            color: '#009494',
                          },
                        }}
                      />
                    }
                    label="No middle name"
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Address Information Card */}
          <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #94e3e3' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader 
                icon={<HomeIcon />} 
                title="Address Information" 
                subtitle="Primary home address"
              />
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={formData.primaryAddressStreet}
                    onChange={handleChange('primaryAddressStreet')}
                    helperText="Must match address of at least one caregiver"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.primaryAddressCity}
                    onChange={handleChange('primaryAddressCity')}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={formData.primaryAddressZip}
                    onChange={handleZipCodeChange}
                    inputProps={{
                      maxLength: 10, // Allow for ZIP+4 format
                      placeholder: "12345"
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Demographics & School Information Card */}
          <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #94e3e3' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader 
                icon={<SchoolIcon />} 
                title="Demographics & School Information" 
                subtitle="Personal details and educational background"
              />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth sx={{ minWidth: 120 }}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender}
                      onChange={handleChange('gender')}
                      label="Gender"
                    >
                      <MenuItem value="MALE">Male</MenuItem>
                      <MenuItem value="FEMALE">Female</MenuItem>
                      <MenuItem value="NON_BINARY">Non-Binary</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="School District"
                    value={formData.district}
                    onChange={handleChange('district')}
                    placeholder="Enter school district"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="School"
                    value={formData.school}
                    onChange={handleChange('school')}
                    placeholder="Enter school name"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 160 }}>
                    <InputLabel>Graduation Year</InputLabel>
                    <Select
                      value={formData.graduationYear}
                      onChange={handleChange('graduationYear')}
                      label="Graduation Year"
                    >
                      {graduationYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ minWidth: 120 }}>
                    <InputLabel>T-shirt Size</InputLabel>
                    <Select
                      value={formData.tshirtSize}
                      onChange={handleChange('tshirtSize')}
                      label="T-shirt Size"
                    >
                      {tshirtSizes.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Roles & Permissions Card */}
          <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #94e3e3' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader 
                icon={<SecurityIcon />} 
                title="Roles & Permissions" 
                subtitle="Assign user roles and access levels"
              />
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>User Roles</InputLabel>
                    <Select
                      multiple
                      value={formData.roleIds}
                      onChange={handleRoleChange}
                      input={<OutlinedInput label="User Roles" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((roleId) => {
                            const role = roles.find(r => r.id === roleId);
                            return (
                              <Chip 
                                key={roleId} 
                                label={role?.name || roleId}
                                sx={{
                                  backgroundColor: '#009494',
                                  color: 'white',
                                  '& .MuiChip-deleteIcon': {
                                    color: 'white',
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Preferences & Status Card */}
          <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #94e3e3' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader 
                icon={<SettingsIcon />} 
                title="Preferences & Status" 
                subtitle="User preferences and account status"
              />
              
              <Grid container spacing={3}>
                {hasStudentRole && (
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.seekingTeam}
                          onChange={handleChange('seekingTeam')}
                          sx={{
                            color: '#009494',
                            '&.Mui-checked': {
                              color: '#009494',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Seeking Team
                          </Typography>
                          <Tooltip title="Student is looking for team members to join their project">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: '#949494' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Grid>
                )}
                
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.optInCommunications}
                        onChange={handleChange('optInCommunications')}
                        sx={{
                          color: '#009494',
                          '&.Mui-checked': {
                            color: '#009494',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Opt-in to Communications
                        </Typography>
                        <Tooltip title="User has agreed to receive communications">
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <InfoIcon fontSize="small" sx={{ color: '#949494' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={handleChange('isActive')}
                        sx={{
                          color: '#009494',
                          '&.Mui-checked': {
                            color: '#009494',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active User
                        </Typography>
                        <Tooltip title="User account is active and can access the system">
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <InfoIcon fontSize="small" sx={{ color: '#949494' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end', 
            pt: 2,
            borderTop: '1px solid #94e3e3',
            mt: 2
          }}>
            <Button 
              variant="outlined" 
              onClick={onCancel}
              sx={{
                minWidth: 120,
                borderColor: '#009494',
                color: '#009494',
                '&:hover': {
                  backgroundColor: '#009494',
                  color: 'white',
                },
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                minWidth: 120,
                backgroundColor: '#009494',
                '&:hover': {
                  backgroundColor: '#004343',
                },
              }}
            >
              {user ? 'Update User' : 'Create User'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default UserForm; 